import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { errorHandler } from '@/utils/functions/errorHandler';
import { IChecklistNotesResponse, IChecklistNoteResponse, ICreateChecklistNotePayload } from '@/types/checklist.types';
import { toast } from 'react-toastify';

interface UseGetChecklistNotesParams {
  tenantId: string | number | undefined;
  instanceId: number | undefined;
  pageSize?: number;
}

export const useGetChecklistNotes = ({ tenantId, instanceId, pageSize = 20 }: UseGetChecklistNotesParams) => {
  return useInfiniteQuery({
    queryKey: ['checklist-notes', tenantId, instanceId],
    queryFn: async ({ pageParam = 1 }) => {
      if (!tenantId || !instanceId) {
        throw new Error('Tenant ID and Instance ID are required');
      }

      try {
        const { data } = await axiosInstance.get<IChecklistNotesResponse>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/checklist-instances/${instanceId}/notes`,
          {
            params: { page: pageParam, page_size: pageSize },
          },
        );
        return data.data;
      } catch (error) {
        errorHandler(error);
        throw error;
      }
    },
    getNextPageParam: lastPage => {
      const { page, total_pages } = lastPage.pagination;
      return page < total_pages ? page + 1 : undefined;
    },
    enabled: !!tenantId && !!instanceId,
    staleTime: 0,
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });
};

interface UseCreateChecklistNoteParams {
  tenantId: string | number;
  instanceId: number;
  payload: ICreateChecklistNotePayload;
}

export const useCreateChecklistNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, instanceId, payload }: UseCreateChecklistNoteParams) => {
      try {
        const { data } = await axiosInstance.post<IChecklistNoteResponse>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/checklist-instances/${instanceId}/notes`,
          payload,
        );
        return data.data;
      } catch (error) {
        errorHandler(error);
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      toast.success('Catatan berhasil dibuat');
      queryClient.invalidateQueries(['checklist-notes', variables.tenantId, variables.instanceId]);
    },
    onError: () => {
      toast.error('Gagal membuat catatan');
    },
  });
};

interface UseGetChecklistNoteParams {
  tenantId: string | number | undefined;
  instanceId: number | undefined;
  noteId: number | undefined;
}

export const useGetChecklistNote = ({ tenantId, instanceId, noteId }: UseGetChecklistNoteParams) => {
  return useInfiniteQuery({
    queryKey: ['checklist-note', tenantId, instanceId, noteId],
    queryFn: async () => {
      if (!tenantId || !instanceId || !noteId) {
        throw new Error('Tenant ID, Instance ID, and Note ID are required');
      }

      try {
        const { data } = await axiosInstance.get<IChecklistNoteResponse>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/checklist-instances/${instanceId}/notes/${noteId}`,
        );
        return data.data;
      } catch (error) {
        errorHandler(error);
        throw error;
      }
    },
    enabled: !!tenantId && !!instanceId && !!noteId,
    staleTime: 0,
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });
};
